import { forwardRef } from 'react'

import { SKY_HEIGHT, SKY_WIDTH, STAR_PLACEMENTS } from './sky-data.ts'

const STAR_COUNT = 20
const VIEWBOX = `0 0 ${String(SKY_WIDTH)} ${String(SKY_HEIGHT)}`
/** Keeps the field in the hero's upper band, clear of the greeting. */
const STAR_BAND = 0.62

/**
 * The hero sky: static markup, class-driven defaults, custom properties for
 * everything the clock changes.
 *
 * Every value the hook writes has a STATIC DEFAULT IN CSS — `--sky-top` falls back
 * to a daytime blue, `--sky-sun-opacity` to a visible sun. That is what makes the
 * no-JavaScript path render a real sky rather than a black rectangle, and it is
 * why the hook overrides custom properties instead of the declarations themselves.
 *
 * Geometry and `mask`/`filter` references are presentation attributes; colour and
 * animation come from classes. Same discipline as the scene system, same reason.
 */
export const Sky = forwardRef<HTMLDivElement>(function Sky(_props, ref) {
  const stars = STAR_PLACEMENTS.slice(0, STAR_COUNT)

  return (
    <div className="ceu" ref={ref} aria-hidden="true">
      <svg
        className="ceu__svg"
        viewBox={VIEWBOX}
        preserveAspectRatio="xMidYMid slice"
        aria-hidden="true"
        focusable="false"
      >
        <defs>
          <linearGradient id="sky-gradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" className="ceu__stop-top" />
            <stop offset="0.55" className="ceu__stop-mid" />
            <stop offset="1" className="ceu__stop-low" />
          </linearGradient>

          <linearGradient id="sky-dissolve" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#FFFFFF" stopOpacity="1" />
            <stop offset="0.42" stopColor="#FFFFFF" stopOpacity="1" />
            <stop offset="1" stopColor="#FFFFFF" stopOpacity="0" />
          </linearGradient>

          <mask id="sky-mask">
            <rect x="0" y="0" width={SKY_WIDTH} height={SKY_HEIGHT} fill="url(#sky-dissolve)" />
          </mask>

          <radialGradient id="sky-sun-glow">
            <stop offset="0" className="ceu__stop-sun" stopOpacity="0.85" />
            <stop offset="1" className="ceu__stop-sun" stopOpacity="0" />
          </radialGradient>

          <radialGradient id="sky-aurora-glow">
            <stop offset="0" className="ceu__stop-aurora" stopOpacity="0.9" />
            <stop offset="1" className="ceu__stop-aurora" stopOpacity="0" />
          </radialGradient>

          {/* The crescent: a second disc punched out of the first. */}
          <mask id="sky-crescent">
            <circle cx="0" cy="0" r="38" fill="#FFFFFF" />
            <circle cx="16" cy="-12" r="32" fill="#000000" />
          </mask>
        </defs>

        <g mask="url(#sky-mask)">
          <rect x="0" y="0" width={SKY_WIDTH} height={SKY_HEIGHT} fill="url(#sky-gradient)" />

          <g className="ceu__estrelas">
            {stars.map((star) => (
              <circle
                key={`${String(star.x)}-${String(star.y)}`}
                className="ceu__estrela ceu__estrela--cintila"
                cx={star.x}
                cy={star.y * STAR_BAND}
                r={star.s * 0.6}
              />
            ))}
          </g>

          <ellipse className="ceu__aurora" rx="360" ry="150" fill="url(#sky-aurora-glow)" />

          <g className="ceu__lua">
            <circle className="ceu__lua-nucleo" cx="0" cy="0" r="38" mask="url(#sky-crescent)" />
          </g>

          <g className="ceu__sol">
            <circle cx="0" cy="0" r="150" fill="url(#sky-sun-glow)" />
            <circle className="ceu__sol-nucleo" cx="0" cy="0" r="44" />
          </g>
        </g>
      </svg>
    </div>
  )
})
